import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, PhoneIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [mobilePlansOpen, setMobilePlansOpen] = useState(false);
  const [mobilePhoneOpen, setMobilePhoneOpen] = useState(false);
  const plansRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setPlansOpen(false);
    setPhoneOpen(false);
    setMobilePlansOpen(false);
    setMobilePhoneOpen(false);
    setIsOpen(false);
  }, [location.pathname]);

  const navigationBefore = [{ name: 'Home', href: '/' }];
  const navigationAfter = [
    { name: 'Support', href: '/support' },
    { name: 'How It Works', href: '/how-it-works' },
    { name: 'Network Status', href: '/tower-status' },
  ];

  const plansMenu = [
    { name: 'Kingman, AZ', href: '/internet-kingman-az' },
    { name: 'Golden Valley, AZ', href: '/internet-golden-valley-az' },
  ];

  const phoneMenu = [
    { name: 'Kingman, AZ', href: '/phone-kingman-az' },
    { name: 'Golden Valley, AZ', href: '/phone-golden-valley-az' },
  ];

  const plansActive = plansMenu.some((item) => location.pathname === item.href);
  const phoneActive = phoneMenu.some((item) => location.pathname === item.href);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (plansRef.current && !plansRef.current.contains(e.target as Node)) {
        setPlansOpen(false);
      }
      if (phoneRef.current && !phoneRef.current.contains(e.target as Node)) {
        setPhoneOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const linkClass = (active: boolean) =>
    `relative px-2 lg:px-3 py-2 text-xs lg:text-xs xl:text-sm font-medium transition-colors whitespace-nowrap ${
      active ? 'text-white' : 'text-white/75 hover:text-white'
    }`;

  const mobileLinkClass = (active: boolean) =>
    `block px-3 py-2 rounded-md text-base font-medium ${
      active ? 'bg-blue-600/20 text-white' : 'text-white/80 hover:text-white hover:bg-white/5'
    }`;

  const dropdownLinkClass = (href: string) =>
    `block px-4 py-2.5 text-sm font-medium ${
      location.pathname === href
        ? 'bg-blue-600/20 text-blue-300'
        : 'text-white/80 hover:bg-white/5 hover:text-white'
    }`;

  const mobileSubLinkClass = (href: string) =>
    `block px-3 py-2 rounded-md text-sm font-medium ${
      location.pathname === href
        ? 'bg-blue-600/20 text-blue-300'
        : 'text-white/70 hover:text-white hover:bg-white/5'
    }`;

  const isHome = location.pathname === '/' || location.pathname === '/home-draft';
  const isCityPlansMobile =
    location.pathname === '/internet-golden-valley-az' ||
    location.pathname === '/internet-kingman-az';

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 mobile-navbar ${
        isHome ? 'mobile-navbar-home' : ''
      } ${
        isHome && !scrolled && !isOpen
          ? 'bg-transparent border-b border-transparent'
          : isHome
            ? 'bg-black/30 backdrop-blur-md border-b border-white/10'
            : 'bg-[#0a0f1a] border-b border-white/10 md:bg-[#0a0f1a]/90 md:backdrop-blur-md'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center h-20 ${
            isCityPlansMobile ? 'justify-center md:justify-between relative' : 'justify-between'
          }`}
        >
          <Link
            to="/"
            onClick={() => window.scrollTo(0, 0)}
            className={`flex items-center mobile-logo ${
              isCityPlansMobile
                ? 'absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0'
                : ''
            }`}
          >
            <div className="flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}/images/AirmaxLogoWhite.png`}
                alt="Airmax Internet"
                className="h-14 w-auto md:h-16"
              />
            </div>
          </Link>

          <div className="hidden lg:block">
            <div className="ml-6 flex items-baseline space-x-1 lg:space-x-2 xl:space-x-3">
              {navigationBefore.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => window.scrollTo(0, 0)}
                  className={linkClass(location.pathname === item.href)}
                >
                  {item.name}
                  {location.pathname === item.href && (
                    <span className="absolute left-2 right-2 -bottom-0.5 h-0.5 rounded-full bg-blue-500" />
                  )}
                </Link>
              ))}

              <div className="relative" ref={plansRef}>
                <button
                  type="button"
                  onClick={() => {
                    setPlansOpen((v) => !v);
                    setPhoneOpen(false);
                  }}
                  className={`inline-flex items-center ${linkClass(plansActive || plansOpen)}`}
                  aria-expanded={plansOpen}
                  aria-haspopup="true"
                >
                  Internet Plans
                  <ChevronDownIcon
                    className={`ml-1 h-4 w-4 transition-transform ${plansOpen ? 'rotate-180' : ''}`}
                  />
                  {plansActive && (
                    <span className="absolute left-2 right-2 -bottom-0.5 h-0.5 rounded-full bg-blue-500" />
                  )}
                </button>
                {plansOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#111827] shadow-xl py-2 z-50">
                    {plansMenu.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => {
                          setPlansOpen(false);
                          window.scrollTo(0, 0);
                        }}
                        className={dropdownLinkClass(item.href)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative" ref={phoneRef}>
                <button
                  type="button"
                  onClick={() => {
                    setPhoneOpen((v) => !v);
                    setPlansOpen(false);
                  }}
                  className={`inline-flex items-center ${linkClass(phoneActive || phoneOpen)}`}
                  aria-expanded={phoneOpen}
                  aria-haspopup="true"
                >
                  Phone Service
                  <ChevronDownIcon
                    className={`ml-1 h-4 w-4 transition-transform ${phoneOpen ? 'rotate-180' : ''}`}
                  />
                  {phoneActive && (
                    <span className="absolute left-2 right-2 -bottom-0.5 h-0.5 rounded-full bg-blue-500" />
                  )}
                </button>
                {phoneOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#111827] shadow-xl py-2 z-50">
                    {phoneMenu.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => {
                          setPhoneOpen(false);
                          window.scrollTo(0, 0);
                        }}
                        className={dropdownLinkClass(item.href)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navigationAfter.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => window.scrollTo(0, 0)}
                  className={linkClass(location.pathname === item.href)}
                >
                  {item.name}
                  {location.pathname === item.href && (
                    <span className="absolute left-2 right-2 -bottom-0.5 h-0.5 rounded-full bg-blue-500" />
                  )}
                </Link>
              ))}

              <a
                href="https://www.airmaxbilling.com/crm/login"
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass(false)}
              >
                My Account
              </a>
              <Link
                to="/about"
                onClick={() => window.scrollTo(0, 0)}
                className={linkClass(location.pathname === '/about')}
              >
                About
                {location.pathname === '/about' && (
                  <span className="absolute left-2 right-2 -bottom-0.5 h-0.5 rounded-full bg-blue-500" />
                )}
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:928-565-6707"
              className="flex items-center text-white hover:text-blue-300 font-semibold transition-colors"
            >
              <PhoneIcon className="h-5 w-5 mr-2 text-blue-400" />
              928-565-6707
            </a>
          </div>

          <div
            className={`md:hidden ${
              isCityPlansMobile ? 'absolute right-0 top-1/2 -translate-y-1/2' : ''
            }`}
          >
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              {isOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden bg-black/50 backdrop-blur-md border-t border-white/10 mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navigationBefore.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={mobileLinkClass(location.pathname === item.href)}
                onClick={() => {
                  setIsOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                {item.name}
              </Link>
            ))}

            <button
              type="button"
              onClick={() => setMobilePlansOpen((v) => !v)}
              className={`w-full flex items-center justify-between ${mobileLinkClass(plansActive || mobilePlansOpen)}`}
            >
              Internet Plans
              <ChevronDownIcon className={`h-5 w-5 transition-transform ${mobilePlansOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobilePlansOpen && (
              <div className="ml-3 border-l border-white/10 pl-2 space-y-1">
                {plansMenu.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={mobileSubLinkClass(item.href)}
                    onClick={() => {
                      setIsOpen(false);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setMobilePhoneOpen((v) => !v)}
              className={`w-full flex items-center justify-between ${mobileLinkClass(phoneActive || mobilePhoneOpen)}`}
            >
              Phone Service
              <ChevronDownIcon className={`h-5 w-5 transition-transform ${mobilePhoneOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobilePhoneOpen && (
              <div className="ml-3 border-l border-white/10 pl-2 space-y-1">
                {phoneMenu.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={mobileSubLinkClass(item.href)}
                    onClick={() => {
                      setIsOpen(false);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}

            {navigationAfter.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={mobileLinkClass(location.pathname === item.href)}
                onClick={() => {
                  setIsOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                {item.name}
              </Link>
            ))}

            <a
              href="https://www.airmaxbilling.com/crm/login"
              target="_blank"
              rel="noopener noreferrer"
              className={mobileLinkClass(false)}
              onClick={() => setIsOpen(false)}
            >
              My Account
            </a>
            <Link
              to="/about"
              className={mobileLinkClass(location.pathname === '/about')}
              onClick={() => {
                setIsOpen(false);
                window.scrollTo(0, 0);
              }}
            >
              About
            </Link>
            <div className="pt-4 pb-3 border-t border-white/10">
              <a
                href="tel:928-565-6707"
                className="flex items-center text-white hover:text-blue-300 font-semibold px-3 py-2"
              >
                <PhoneIcon className="h-5 w-5 mr-2 text-blue-400" />
                928-565-6707
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
